import getDeviceFingerprint from '../utils/deviceFingerprint';

const storage = {
  get(key) {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn('Unable to read localStorage key:', key, error);
      return null;
    }
  },
  set(key, value) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      if (value === null || typeof value === 'undefined') {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('Unable to write localStorage key:', key, error);
    }
  }
};
// API Service for AIIA - Based on TheVillage pattern
const FRONTDOOR_HOSTS = new Set(['www.aiiai.org', 'aiiai.org']);

const sanitizeBaseUrl = (url = '') => {
  if (!url) return '';
  const trimmed = url.trim();
  if (trimmed === '/') return '';
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const getStoredAccessToken = () => storage.get('auth_token');
const getStoredRefreshToken = () => storage.get('auth_refresh_token');

const persistTokensLocally = (payload = {}) => {
  const accessToken = payload.accessToken || payload.token || null;
  if (accessToken) {
    storage.set('auth_token', accessToken);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'refreshToken')) {
    storage.set('auth_refresh_token', payload.refreshToken || null);
  }

  if (typeof payload.expiresIn === 'number' && Number.isFinite(payload.expiresIn)) {
    const expiresAt = Date.now() + payload.expiresIn * 1000;
    storage.set('auth_token_expires', String(expiresAt));
  }

  if (payload.refreshTokenExpiresAt) {
    storage.set('auth_refresh_expires', payload.refreshTokenExpiresAt);
  }
};

const isAbsoluteUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const shouldWarnDuplicateApi = () => {
  const envFlag = import.meta.env.VITE_LOG_DUP_API_WARNINGS;
  if (typeof envFlag === 'string') {
    const normalized = envFlag.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }
  return Boolean(import.meta.env?.DEV);
};

const extractHostname = (value) => {
  if (!isAbsoluteUrl(value)) return '';
  try {
    return new URL(value).hostname?.toLowerCase() || '';
  } catch {
    return '';
  }
};

const isFrontdoorHost = (hostname) => FRONTDOOR_HOSTS.has(hostname?.toLowerCase?.() || hostname);

const isFrontdoorUrl = (value) => isFrontdoorHost(extractHostname(value));

const joinUrl = (base, endpoint) => {
  if (!endpoint) return base || '';

  // Absolute endpoint wins
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  const sanitizedBase = sanitizeBaseUrl(base);

  // If endpoint already starts with /api and base is relative or missing, use endpoint as-is
  if (endpoint.startsWith('/api') && (!sanitizedBase || sanitizedBase.startsWith('/'))) {
    return endpoint;
  }

  // If base exists (absolute or relative) join it with endpoint
  if (sanitizedBase) {
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const baseHasApiSuffix = sanitizedBase.toLowerCase().endsWith('/api');
    const pathStartsWithApi = path === '/api' || path.startsWith('/api/');
    const dedupedPath = baseHasApiSuffix && pathStartsWithApi ? path.slice(4) || '' : path;
    return `${sanitizedBase}${dedupedPath}`;
  }

  // Fallback to endpoint when no base provided
  return endpoint;
};

const PRODUCTION_DIRECT_API = 'https://api.aiiai.org';
const ENV_API_URL = import.meta.env.VITE_API_URL?.trim();
const DIRECT_ENV_API_URL = import.meta.env.VITE_DIRECT_API_URL?.trim();

const isBrowserEnv = () => typeof window !== 'undefined';
const isProductionHost = () => {
  if (!isBrowserEnv()) return false;
  const host = window.location.hostname?.toLowerCase();
  return isFrontdoorHost(host);
};

const resolveDirectApiFallback = () => {
  // In browser on HTTPS, never fall back to HTTP URLs (Mixed Content)
  if (isBrowserEnv() && window.location.protocol === 'https:') {
    if (DIRECT_ENV_API_URL && DIRECT_ENV_API_URL.startsWith('http://')) {
      console.warn('⚠️ Ignoring HTTP VITE_DIRECT_API_URL on HTTPS page, using production direct API');
      return PRODUCTION_DIRECT_API;
    }
  }
  return DIRECT_ENV_API_URL || PRODUCTION_DIRECT_API;
};

const resolveBaseURL = () => {
  const isBrowser = typeof window !== 'undefined';
  const envUrl = ENV_API_URL;
  const directEnvUrl = DIRECT_ENV_API_URL;

  if (isBrowser) {
    const hostname = window.location.hostname?.toLowerCase();
    const productionHost = isFrontdoorHost(hostname);

    // On production hosts prefer explicit env overrides that point somewhere other than the frontdoor domain.
    if (productionHost) {
      if (envUrl) {
        if (envUrl.startsWith('/')) {
          return envUrl;
        }
        if (isFrontdoorUrl(envUrl)) {
          console.warn('⚠️ VITE_API_URL points to www.aiiai.org; defaulting to direct API host until the proxy is configured.');
        } else {
          return envUrl;
        }
      }

      if (directEnvUrl) {
        return directEnvUrl;
      }

      console.warn('⚠️ Using frontdoor /api proxy for production host');
      return '/api';
    }
  }

  if (envUrl) {
    if (isBrowser && window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      return '/api';
    }
    return envUrl;
  }

  if (directEnvUrl) {
    return directEnvUrl;
  }

  if (isBrowser) {
    return '/api';
  }

  return 'http://127.0.0.1:5000';
};

class AIIAAPIService {
  constructor() {
    this.baseURL = resolveBaseURL();
    this.isRelativeBase = !isAbsoluteUrl(this.baseURL);
    this.warnOnDuplicateApi = shouldWarnDuplicateApi();
    this.initialized = false;
    this.authHandlers = {
      getAccessToken: () => getStoredAccessToken(),
      getRefreshToken: () => getStoredRefreshToken(),
      onTokensUpdated: null,
      onUnauthorized: null,
    };
    this.refreshPromise = null;
    console.log('AIIA API Service initialized with baseURL:', this.baseURL);
  }

  registerAuthHandlers(handlers = {}) {
    this.authHandlers = {
      ...this.authHandlers,
      ...handlers
    };
  }

  shouldUseDirectFallback() {
    if (!isBrowserEnv()) return false;
    if (this.isRelativeBase && isProductionHost()) {
      return true;
    }

    if (isAbsoluteUrl(this.baseURL) && isFrontdoorUrl(this.baseURL)) {
      return true;
    }

    return false;
  }

  useDirectApiFallback(reason = 'fallback triggered') {
    if (!this.shouldUseDirectFallback()) {
      return false;
    }

    const fallbackUrl = resolveDirectApiFallback();
    if (this.baseURL === fallbackUrl) {
      return false;
    }

    console.warn(`⚠️ Switching to direct API domain (${fallbackUrl}) due to ${reason}`);
    this.baseURL = fallbackUrl;
    this.isRelativeBase = false;
    this.initialized = false;
    return true;
  }

  async ensureInitialized() {
    if (this.initialized) return true;

    const tryHealthCheck = async () => {
      try {
        const healthUrl = joinUrl(this.baseURL, '/health');
        const response = await fetch(healthUrl, { method: 'GET', signal: AbortSignal.timeout(2000) });
        if (response.ok) {
          this.initialized = true;
          console.log('✅ Backend connection verified');
          return true;
        }
        console.warn(`⚠️ Health check returned ${response.status} for ${healthUrl}`);
      } catch (error) {
        console.warn('⚠️ Backend health check failed, will retry on next request:', error.message);
      }
      return false;
    };

    if (await tryHealthCheck()) {
      return true;
    }

    if (this.useDirectApiFallback('health check failure')) {
      return await tryHealthCheck();
    }

    return false;
  }

  async performRefresh(refreshTokenOverride) {
    const refreshToken =
      refreshTokenOverride ||
      (this.authHandlers?.getRefreshToken?.() ?? getStoredRefreshToken());

    if (!refreshToken) {
      return null;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const fingerprint = typeof window !== 'undefined' ? getDeviceFingerprint() : undefined;

    this.refreshPromise = (async () => {
      await this.ensureInitialized();
      const url = joinUrl(this.baseURL, '/api/auth/refresh');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(fingerprint ? { 'x-device-fingerprint': fingerprint } : {})
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`Refresh failed: ${response.status} - ${errorText || 'unknown error'}`);
        if (this.authHandlers?.onUnauthorized) {
          try {
            this.authHandlers.onUnauthorized(error);
          } catch (handlerError) {
            console.warn('Auth handler onUnauthorized threw error:', handlerError);
          }
        }
        throw error;
      }

      const payload = await response.json();

      if (this.authHandlers?.onTokensUpdated) {
        try {
          this.authHandlers.onTokensUpdated(payload);
        } catch (handlerError) {
          console.warn('Auth handler onTokensUpdated threw error:', handlerError);
        }
      } else {
        persistTokensLocally(payload);
      }

      return payload;
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async refreshAccessToken(refreshTokenOverride) {
    const payload = await this.performRefresh(refreshTokenOverride);
    return payload?.accessToken || payload?.token || null;
  }

  async manualRefresh(refreshTokenOverride) {
    return await this.performRefresh(refreshTokenOverride);
  }

  async request(endpoint, options = {}) {
    if (!endpoint) {
      throw new Error('Endpoint is required');
    }

    // Ensure backend is reachable before making requests
    await this.ensureInitialized();

    const url = joinUrl(this.baseURL, endpoint);
    
    if (this.warnOnDuplicateApi) {
      // Debug logging to catch URL construction issues
      try {
        const pathOnly = url.replace(/^https?:\/\/[^/]+/i, '');
        if (pathOnly.includes('/api/api')) {
          console.error('⚠️ Duplicate /api detected in URL path:', url);
          console.error('   baseURL:', this.baseURL);
          console.error('   endpoint:', endpoint);
        }
      } catch (err) {
        // Fallback to original string check if URL parsing fails (e.g., relative URL)
        if (url.includes('/api/api')) {
          console.error('⚠️ Duplicate /api detected in URL:', url);
          console.error('   baseURL:', this.baseURL);
          console.error('   endpoint:', endpoint);
        }
      }
    }

  const { headers: optionHeaders = {}, skipAuthRetries = false, ...restOptions } = options;
  const methodName = restOptions.method || 'GET';

    const fingerprint = typeof window !== 'undefined' ? getDeviceFingerprint() : undefined;
    let token = this.authHandlers?.getAccessToken?.() ?? getStoredAccessToken();

    const config = {
      headers: {
        'Accept': 'application/json',
        ...(restOptions.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(typeof window !== 'undefined' ? { 'Origin': window.location.origin } : {}),
        ...(fingerprint ? { 'x-device-fingerprint': fingerprint } : {}),
        ...(token && { 'Authorization': `Bearer ${token}` }), // Add token if exists
        ...optionHeaders,
      },
      credentials: 'include',
      mode: 'cors',
      ...restOptions,
    };

    // Retry logic for transient errors (502, 503, 504, network issues)
    const maxRetries = 2;
    const retryableStatuses = [404, 502, 503, 504];
    let lastError = null;
    let attemptedRefresh = false;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          // Only log retries at debug level - don't spam console
          if (import.meta.env.DEV) {
            console.log(`🔄 Retry attempt ${attempt}/${maxRetries} for ${url}`);
          }
          await new Promise(resolve => setTimeout(resolve, 500 * attempt)); // Exponential backoff
        }

        if (import.meta.env.DEV) {
          console.log(`🔌 API Request: ${methodName} ${url}`);
        }
        
        const response = await fetch(url, config);

        if (response.status === 401 && !skipAuthRetries) {
          lastError = new Error('Unauthorized');
          if (!attemptedRefresh) {
            attemptedRefresh = true;
            try {
              const refreshedToken = await this.refreshAccessToken();
              if (refreshedToken) {
                token = refreshedToken;
                config.headers.Authorization = `Bearer ${refreshedToken}`;
                attempt -= 1;
                continue;
              } else {
                // No refresh token available or refresh failed silently
                if (this.authHandlers?.onUnauthorized) {
                   this.authHandlers.onUnauthorized(new Error('Session expired (no refresh token)'));
                }
              }
            } catch (refreshError) {
              lastError = refreshError;
              // Refresh failed with error
              if (this.authHandlers?.onUnauthorized) {
                 this.authHandlers.onUnauthorized(refreshError);
              }
            }
          }
        }
        
        // Retry on transient errors (502, 503, 504, 404)
        if (retryableStatuses.includes(response.status)) {
          lastError = new Error(`API Error: ${response.status}`);
          
          // For 404, try switching to direct API
          if (response.status === 404) {
            const switched = this.useDirectApiFallback(`404 response from ${url}`);
            if (switched) {
              continue;
            }
          }

          // Retry silently for transient errors
          if (attempt < maxRetries) {
            continue;
          }
        }
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ API Error (${response.status}):`, errorText);
          throw new Error(`API Error: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        if (import.meta.env.DEV) {
          console.log(`✅ API Response:`, data);
        }
        return data;
        
      } catch (error) {
        lastError = error;
        // Only log on final failure
        if (attempt === maxRetries) {
          console.error('❌ API Request Failed after retries:', error.message);
          throw error;
        }
        // Network errors - retry silently
      }
    }

    throw lastError;
  }

  // Health Check
  async healthCheck() {
    return await this.request('/api/health');
  }

  // Video Analysis Methods
  async uploadVideo(file, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      if (typeof onProgress === 'function') {
        onProgress(0);
      }
      console.log(`📤 Uploading video: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      
      const url = joinUrl(this.baseURL, '/api/upload/video');
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (typeof onProgress === 'function') {
        onProgress(1);
      }
      console.log('✅ Upload successful:', data);
      return data;
      
    } catch (error) {
      if (typeof onProgress === 'function') {
        onProgress(-1, error);
      }
      console.error('❌ Upload failed:', error);
      throw error;
    }
  }

  // Generic File Upload
  async uploadFile(file, type = 'attachment') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const url = joinUrl(this.baseURL, '/api/upload/file');
    const token = this.authHandlers?.getAccessToken?.() ?? getStoredAccessToken();
    
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  // Communal Studio Methods
  async getProjectCollaborators(projectId) {
    return this.request(`/api/audio-projects/${projectId}/collaborators`);
  }

  async addProjectCollaborator(projectId, userId, role = 'editor') {
    return this.request(`/api/audio-projects/${projectId}/collaborators`, {
      method: 'POST',
      body: JSON.stringify({ userId, role })
    });
  }

  async getProjectMessages(projectId) {
    return this.request(`/api/audio-projects/${projectId}/messages`);
  }

  async sendProjectMessage(projectId, content) {
    return this.request(`/api/audio-projects/${projectId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content })
    });
  }

  async saveAudioProject(file, metadata = {}) {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('title', metadata.title || file.name);
    formData.append('description', metadata.description || '');
    formData.append('duration', metadata.duration || 0);
    formData.append('is_public', metadata.is_public || false);

    try {
      console.log(`📤 Saving audio project: ${file.name}`);
      
      const url = joinUrl(this.baseURL, '/api/audio-projects');
      const token = getStoredAccessToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Save failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Audio project saved:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Save failed:', error);
      throw error;
    }
  }

  async getAnalysisResult(jobId) {
    try {
      console.log(`📥 Fetching analysis result for job: ${jobId}`);
      return await this.request(`/api/analyze/${jobId}`);
    } catch (error) {
      console.error(`❌ Failed to get analysis result for ${jobId}:`, error);
      throw error;
    }
  }

  async listVideos({ limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/videos?limit=${limit}&offset=${offset}`);
  }

  async deleteVideo(jobId) {
    return await this.request(`/api/videos/${jobId}`, {
      method: 'DELETE'
    });
  }

  async startAnalysis(jobId, options = {}) {
    return await this.request('/api/analysis/start', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId, ...options })
    });
  }

  async listJobs({ limit = 100, offset = 0, status = '' } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (status) {
      params.append('status', status);
    }
    return await this.request(`/api/jobs?${params.toString()}`);
  }

  async listAnalysisResults({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/analysis/results?limit=${limit}&offset=${offset}`);
  }

  async saveAnalysisResult(jobId, payload) {
    return await this.request(`/api/analysis/${jobId}/results`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async attachToDataset(jobId, payload) {
    return await this.request(`/api/analysis/${jobId}/dataset`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async listDatasets({ limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/datasets?limit=${limit}&offset=${offset}`);
  }

  async listNodes() {
    try {
      console.log('📋 Fetching node status');
      return await this.request('/api/nodes');
    } catch (error) {
      console.error('❌ Failed to fetch nodes:', error);
      throw error;
    }
  }

  // ============ Social Media Features ============

  // Authentication
  async login(email, password, rememberMe = false) {
    return await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username: email, password, rememberMe }) // Backend expects 'username' (can be email or username)
    });
  }

  async signup(username, email, password, rememberMe = false) {
    return await this.request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ username, email, password, rememberMe })
    });
  }

  async logout(refreshToken) {
    const payload = refreshToken ? { refreshToken } : {};
    return await this.request('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuthRetries: true
    });
  }

  async refreshTokens(refreshTokenOverride) {
    return await this.manualRefresh(refreshTokenOverride);
  }

  async getCurrentUser() {
    return await this.request('/api/auth/me');
  }

  async updateProfile(updates) {
    return await this.request('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(joinUrl(this.baseURL, '/api/users/me/avatar'), {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload avatar');
    }

    return await response.json();
  }

  // Feed & Posts
  async getFeed({ limit = 10, offset = 0, algorithm = 'personalized' } = {}) {
    return await this.request(`/api/feed?limit=${limit}&offset=${offset}&algorithm=${algorithm}`);
  }

  async getPost(postId) {
    return await this.request(`/api/posts/${postId}`);
  }

  async createPost(formData) {
    // formData should include: video file, caption, hashtags, visibility
    // Use direct API URL for video uploads to bypass Netlify's 26MB limit
    const envUploadUrl = import.meta.env.VITE_DIRECT_UPLOAD_URL?.trim();
    const directApiUrl = envUploadUrl && envUploadUrl.length > 0
      ? envUploadUrl
      : joinUrl(this.baseURL, '/api/posts');
    
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

  let response;
  try {
    response = await fetch(directApiUrl, {
      method: 'POST',
      headers,
      body: formData, // FormData sets its own Content-Type with boundary
      credentials: 'include',
    });
  } catch (networkError) {
    console.error('❌ Network error during upload:', networkError);
    if (networkError?.message === 'Failed to fetch') {
      throw new Error('Network connection dropped before the upload reached the server. Files above 500MB require compression.');
    }
    throw networkError;
  }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API Error: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      console.error('❌ Post upload failed:', {
        status: response.status,
        endpoint: directApiUrl,
        body: errorText,
      });
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  async deletePost(postId) {
    return await this.request(`/api/posts/${postId}`, { method: 'DELETE' });
  }

  // Engagement
  async trackEngagement(data) {
    return await this.request('/api/engagement', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async likePost(postId) {
    return await this.request(`/api/posts/${postId}/like`, { method: 'POST' });
  }

  async unlikePost(postId) {
    return await this.request(`/api/posts/${postId}/like`, { method: 'DELETE' });
  }

  async getComments(postId, { limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/posts/${postId}/comments?limit=${limit}&offset=${offset}`);
  }

  async createComment(postId, content, parentCommentId = null) {
    const response = await this.request(`/api/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parent_comment_id: parentCommentId })
    });
    return response?.comment ?? response;
  }

  async likeComment(commentId) {
    return await this.request(`/api/comments/${commentId}/like`, { method: 'POST' });
  }

  async unlikeComment(commentId) {
    return await this.request(`/api/comments/${commentId}/like`, { method: 'DELETE' });
  }

  // User profiles & follows
  async getProfile(username) {
    return await this.request(`/api/users/${username}`);
  }

  async getUserPosts(username, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/users/${username}/posts?limit=${limit}&offset=${offset}`);
  }

  async followUser(userId) {
    return await this.request(`/api/users/${userId}/follow`, { method: 'POST' });
  }

  async unfollowUser(userId) {
    return await this.request(`/api/users/${userId}/follow`, { method: 'DELETE' });
  }

  async getFollowers(username, { limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/users/${username}/followers?limit=${limit}&offset=${offset}`);
  }

  async getFollowing(username, { limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/users/${username}/following?limit=${limit}&offset=${offset}`);
  }

  // Subscriptions
  async subscribeToCreator(creatorId, tier = 'basic') {
    return await this.request(`/api/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ creator_id: creatorId, tier })
    });
  }

  async unsubscribeFromCreator(subscriptionId) {
    return await this.request(`/api/subscriptions/${subscriptionId}`, { method: 'DELETE' });
  }

  async getMySubscriptions() {
    return await this.request('/api/subscriptions/me');
  }

  // Stripe Payment Methods
  /**
   * Create a Stripe checkout session for subscription
   * @param {string} tierKey - The subscription tier (premium, pro)
   * @param {string} billingPeriod - 'monthly' or 'yearly'
   * @returns {Promise<{client_secret: string, subscription_id: string}>}
   */
  async createStripeCheckout(tierKey, billingPeriod = 'monthly') {
    return await this.request('/api/subscriptions/checkout/stripe', {
      method: 'POST',
      body: JSON.stringify({ 
        tier_key: tierKey, 
        billing_period: billingPeriod 
      })
    });
  }

  /**
   * Confirm a Stripe payment after successful card input
   * @param {string} paymentIntentId - The Stripe payment intent ID
   * @returns {Promise<{success: boolean, subscription: object}>}
   */
  async confirmStripePayment(paymentIntentId) {
    return await this.request('/api/subscriptions/confirm/stripe', {
      method: 'POST',
      body: JSON.stringify({ payment_intent_id: paymentIntentId })
    });
  }

  /**
   * Cancel current Stripe subscription
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async cancelStripeSubscription() {
    return await this.request('/api/subscriptions/cancel/stripe', {
      method: 'POST'
    });
  }

  /**
   * Get subscription tiers with pricing
   * @returns {Promise<{tiers: Array}>}
   */
  async getSubscriptionTiers() {
    return await this.request('/api/subscriptions/tiers');
  }

  /**
   * Get tier comparison features
   * @returns {Promise<object>}
   */
  async getSubscriptionComparison() {
    return await this.request('/api/subscriptions/compare');
  }

  // Communities (Discord-style)
  async getCommunities({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/communities?limit=${limit}&offset=${offset}`);
  }

  async getCommunity(communityId) {
    return await this.request(`/api/communities/${communityId}`);
  }

  async getCommunityLayout(communityId) {
    if (!communityId) {
      throw new Error('Community ID is required to load layout');
    }
    return await this.request(`/api/communities/${communityId}/layout`);
  }

  async updateCommunityLayout(communityId, payload) {
    if (!communityId) {
      throw new Error('Community ID is required to update layout');
    }
    return await this.request(`/api/communities/${communityId}/layout`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {})
    });
  }

  async getUserCommunity(username) {
    if (!username) {
      throw new Error('Username is required to load community');
    }
    return await this.request(`/api/users/${username}/community`);
  }

  async createCommunity(data) {
    const response = await this.request('/api/communities', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response?.community ?? response;
  }

  async deleteCommunity(communityId) {
    return await this.request(`/api/communities/${communityId}`, { method: 'DELETE' });
  }

  async joinCommunity(communityId) {
    return await this.request(`/api/communities/${communityId}/join`, { method: 'POST' });
  }

  async getCommunityMembers(communityId, { limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/communities/${communityId}/members?limit=${limit}&offset=${offset}`);
  }

  async leaveCommunity(communityId) {
    return await this.request(`/api/communities/${communityId}/leave`, { method: 'POST' });
  }

  // Search endpoints
  async searchUsers(query, { limit = 10, offset = 0 } = {}) {
    return await this.request(`/api/search/users?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  }

  async searchCommunities(query, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/search/communities?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  }

  async searchPosts(query, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/search/posts?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  }

  async searchHashtags(query = '', { limit = 20 } = {}) {
    const queryParam = query ? `q=${encodeURIComponent(query)}&` : '';
    return await this.request(`/api/search/hashtags?${queryParam}limit=${limit}`);
  }

  // User communities
  async getUserCommunities(userId, { limit = 20, offset = 0, type = '' } = {}) {
    // If no userId provided, get current user's communities
    let url = userId 
      ? `/api/users/${userId}/communities?limit=${limit}&offset=${offset}`
      : `/api/users/me/communities?limit=${limit}&offset=${offset}`;
    if (type) url += `&type=${type}`;
    return await this.request(url);
  }

  // Community categories
  async getCommunityCategories() {
    return await this.request('/api/communities/categories');
  }

  // Communities with pagination and category filter
  async getCommunities({ limit = 12, offset = 0, category = '', search = '' } = {}) {
    let url = `/api/communities?limit=${limit}&offset=${offset}`;
    if (category && category !== 'all') url += `&category=${category}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await this.request(url);
  }

  // Free agent categories
  async getAgentCategories() {
    return await this.request('/api/users/free-agents/categories');
  }

  // Free agents (users looking for communities) with category filter
  async getFreeAgents({ limit = 12, offset = 0, category = '' } = {}) {
    let url = `/api/users/free-agents?limit=${limit}&offset=${offset}`;
    if (category && category !== 'all') url += `&category=${category}`;
    return await this.request(url);
  }

  // Channels & Messages
  async getChannels(communityId) {
    const response = await this.request(`/api/communities/${communityId}/channels`);
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.channels)) {
      return response.channels;
    }
    return [];
  }

  async getMessages(channelId, { limit = 50, offset = 0 } = {}) {
    const response = await this.request(`/api/channels/${channelId}/messages?limit=${limit}&offset=${offset}`);
    if (Array.isArray(response)) {
      return response;
    }
    return response?.messages || [];
  }

  async sendMessage(channelId, content, attachmentUrl = null) {
    return await this.request(`/api/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, attachment_url: attachmentUrl })
    });
  }

  async scaffoldCommunityChannels(communityId, payload = {}) {
    if (!communityId) {
      throw new Error('Community ID is required to scaffold channels');
    }
    return await this.request(`/api/communities/${communityId}/channels/scaffold`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async createChannel(communityId, payload = {}) {
    if (!communityId) {
      throw new Error('Community ID is required to create a channel');
    }
    return await this.request(`/api/communities/${communityId}/channels`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateChannel(communityId, channelId, payload = {}) {
    if (!communityId || !channelId) {
      throw new Error('Community ID and channel ID are required to update a channel');
    }
    return await this.request(`/api/communities/${communityId}/channels/${channelId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  }

  async deleteChannel(communityId, channelId) {
    if (!communityId || !channelId) {
      throw new Error('Community ID and channel ID are required to delete a channel');
    }
    return await this.request(`/api/communities/${communityId}/channels/${channelId}`, {
      method: 'DELETE'
    });
  }

  // Direct Messages
  // Messaging (Backend Integration)
  async getThreads({ folder = 'inbox', limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/messaging/threads?folder=${folder}&limit=${limit}&offset=${offset}`);
  }

  async getThreadMessages(threadId, { limit = 50, before } = {}) {
    const query = before ? `&before=${before}` : '';
    return await this.request(`/api/messaging/threads/${threadId}/messages?limit=${limit}${query}`);
  }

  async createThread(recipientId) {
    return await this.request('/api/messaging/threads', {
      method: 'POST',
      body: JSON.stringify({ recipientId })
    });
  }

  async sendThreadMessage(threadId, content) {
    return await this.request(`/api/messaging/threads/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content })
    });
  }

  async getDirectInbox({ tab = 'inbox', limit = 50, offset = 0 } = {}) {
    try {
      const response = await this.getThreads({ folder: tab, limit, offset });
      const conversations = (response.threads || []).map(t => {
         const otherParticipant = t.participants?.[0] || {};
         return {
           id: t.id,
           user_id: otherParticipant.id,
           username: otherParticipant.username,
           display_name: otherParticipant.username, // Fallback
           avatar_url: otherParticipant.avatar_url,
           last_message: t.last_message?.content,
           last_timestamp: t.last_message?.created_at || t.updated_at,
           unread_count: t.last_message?.is_read === false ? 1 : 0,
           status: t.folder
         };
      });
      return { conversations };
    } catch (err) {
      console.warn('Failed to fetch threads:', err);
      return { conversations: [] };
    }
  }

  async searchDirectMessages(query, { limit = 20, offset = 0 } = {}) {
    if (!query?.trim()) {
      return { conversations: [] };
    }
    // TODO: Implement search endpoint in backend
    return { conversations: [] };
  }

  async respondToMessageRequest(conversationId, action = 'accept') {
    // TODO: Implement request handling in backend
    return { success: true };
  }

  async markConversationSpam(conversationId, { isSpam = true } = {}) {
    // TODO: Implement spam marking
    return { success: true };
  }

  async getDirectMessages(threadId, { limit = 50, offset = 0 } = {}) {
    return await this.getThreadMessages(threadId, { limit });
  }

  async sendDirectMessage(recipientId, content, attachmentUrl = null) {
    // recipientId here is likely a User ID. We ensure a thread exists first.
    const { threadId } = await this.createThread(recipientId);
    return await this.sendThreadMessage(threadId, content);
  }

  // Notifications
  async getNotifications({ limit = 50, offset = 0, unread_only = false } = {}) {
    return await this.request(`/api/notifications?limit=${limit}&offset=${offset}&unread_only=${unread_only}`);
  }

  async markNotificationRead(notificationId) {
    return await this.request(`/api/notifications/${notificationId}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return await this.request('/api/notifications/read-all', { method: 'POST' });
  }

  // Hashtags & Search
  async searchHashtag(tag, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/hashtags/${encodeURIComponent(tag)}?limit=${limit}&offset=${offset}`);
  }

  async getTrendingHashtags({ limit = 20 } = {}) {
    return await this.request(`/api/hashtags/trending?limit=${limit}`);
  }

  async searchUsers(query, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/search/users?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  }

  async searchPosts(query, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/search/posts?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  }

  // Poll for analysis completion
  async pollAnalysisResult(jobId, intervalMs = 5000, maxAttempts = 60) {
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const result = await this.getAnalysisResult(jobId);
          
          if (result.status === 'completed') {
            console.log(`✅ Analysis completed after ${attempts} attempts`);
            resolve(result);
          } else if (result.status === 'failed') {
            reject(new Error('Analysis failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Analysis timeout'));
          } else {
            console.log(`⏳ Analysis in progress... (attempt ${attempts}/${maxAttempts})`);
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      poll();
    });
  }

  // ============ Studio / DAW Features ============

  // Studio Projects
  async getStudioProjects({ limit = 50, offset = 0, type = '' } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (type) params.append('type', type);
    return await this.request(`/api/studio/projects?${params.toString()}`);
  }

  async getStudioProject(projectId) {
    return await this.request(`/api/studio/projects/${projectId}`);
  }

  async createStudioProject(data) {
    // data: { name, description, project_type: 'daw'|'video'|'mixed', settings, bpm, time_signature }
    return await this.request('/api/studio/projects', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateStudioProject(projectId, data) {
    return await this.request(`/api/studio/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteStudioProject(projectId) {
    return await this.request(`/api/studio/projects/${projectId}`, { method: 'DELETE' });
  }

  async duplicateStudioProject(projectId) {
    return await this.request(`/api/studio/projects/${projectId}/duplicate`, { method: 'POST' });
  }

  // Project Assets (audio, video, images uploaded to project)
  async getProjectAssets(projectId) {
    return await this.request(`/api/studio/projects/${projectId}/assets`);
  }

  async uploadProjectAsset(projectId, formData) {
    // formData should include file, name, asset_type
    const token = localStorage.getItem('auth_token');
    const url = joinUrl(this.baseURL, `/api/studio/projects/${projectId}/assets`);
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  async deleteProjectAsset(assetId) {
    return await this.request(`/api/studio/assets/${assetId}`, { method: 'DELETE' });
  }

  // Timeline Tracks
  async getProjectTracks(projectId) {
    return await this.request(`/api/studio/projects/${projectId}/tracks`);
  }

  async createProjectTrack(projectId, data) {
    // data: { name, track_type: 'audio'|'video'|'midi', order_index, color, effects }
    return await this.request(`/api/studio/projects/${projectId}/tracks`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateProjectTrack(trackId, data) {
    return await this.request(`/api/studio/tracks/${trackId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteProjectTrack(trackId) {
    return await this.request(`/api/studio/tracks/${trackId}`, { method: 'DELETE' });
  }

  // Timeline Clips
  async getTrackClips(trackId) {
    return await this.request(`/api/studio/tracks/${trackId}/clips`);
  }

  async createTrackClip(trackId, data) {
    // data: { asset_id, name, timeline_start, timeline_duration, source_start, source_end, volume, effects }
    return await this.request(`/api/studio/tracks/${trackId}/clips`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateTrackClip(clipId, data) {
    return await this.request(`/api/studio/clips/${clipId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteTrackClip(clipId) {
    return await this.request(`/api/studio/clips/${clipId}`, { method: 'DELETE' });
  }

  // Effect Presets
  async getEffectPresets({ category = '' } = {}) {
    const params = category ? `?category=${category}` : '';
    return await this.request(`/api/studio/effect-presets${params}`);
  }

  async createEffectPreset(data) {
    // data: { name, effect_type, category, parameters }
    return await this.request('/api/studio/effect-presets', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Export Jobs
  async createExportJob(projectId, data) {
    // data: { export_format: 'mp3'|'wav'|'flac'|'mp4'|'webm', quality_preset, settings }
    return await this.request(`/api/studio/projects/${projectId}/export`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getExportJob(exportId) {
    return await this.request(`/api/studio/exports/${exportId}`);
  }

  async pollExportJob(exportId, intervalMs = 2000, maxAttempts = 120) {
    let attempts = 0;
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const result = await this.getExportJob(exportId);
          if (result.status === 'completed') {
            resolve(result);
          } else if (result.status === 'failed') {
            reject(new Error(result.error_message || 'Export failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Export timeout'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  }

  // Loop Library
  async getLoopLibrary({ category = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (category) params.append('category', category);
    return await this.request(`/api/studio/loops?${params.toString()}`);
  }

  async uploadLoop(formData) {
    const token = localStorage.getItem('auth_token');
    const url = joinUrl(this.baseURL, '/api/studio/loops');
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  // Beat Patterns
  async getBeatPatterns({ limit = 50, offset = 0 } = {}) {
    return await this.request(`/api/studio/beat-patterns?limit=${limit}&offset=${offset}`);
  }

  async saveBeatPattern(data) {
    // data: { name, pattern_data, bpm, time_signature, steps, kit_name, tags }
    return await this.request('/api/studio/beat-patterns', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateBeatPattern(patternId, data) {
    return await this.request(`/api/studio/beat-patterns/${patternId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteBeatPattern(patternId) {
    return await this.request(`/api/studio/beat-patterns/${patternId}`, { method: 'DELETE' });
  }

  // Wallet
  async getWalletBalance() {
    return await this.request('/api/wallet/balance');
  }

  async getWalletTransactions(limit = 20, offset = 0) {
    return await this.request(`/api/wallet/transactions?limit=${limit}&offset=${offset}`);
  }

  async transferTokens(recipientId, amount) {
    return await this.request('/api/wallet/transfer', {
      method: 'POST',
      body: JSON.stringify({ recipientId, amount })
    });
  }

  /**
   * Send a tip to a user/creator
   * @param {number} recipientId - User ID to tip
   * @param {number} amountCents - Amount in cents
   * @param {string} message - Optional tip message
   * @param {string} contentId - Optional content ID (video/post being tipped on)
   */
  async sendTip(recipientId, amountCents, message = null, contentId = null) {
    return await this.request('/api/wallet/tip', {
      method: 'POST',
      body: JSON.stringify({ 
        recipient_id: recipientId, 
        amount_cents: amountCents,
        message,
        content_id: contentId
      })
    });
  }

  /**
   * Request withdrawal to external payment method
   * @param {number} amountCents - Amount in cents (minimum $10.00 = 1000 cents)
   * @param {string} paymentMethod - 'stripe' or 'paypal'
   * @param {object} accountDetails - Payment account details
   */
  async requestWithdrawal(amountCents, paymentMethod = 'stripe', accountDetails = {}) {
    return await this.request('/api/wallet/withdraw', {
      method: 'POST',
      body: JSON.stringify({
        amount_cents: amountCents,
        payment_method: paymentMethod,
        account_details: accountDetails
      })
    });
  }

  // ============ Voice Channels (LiveKit) ============

  /**
   * Get a LiveKit token for joining a voice channel
   * @param {string} channelId - Voice channel ID
   * @param {string} communityId - Community ID
   */
  async getVoiceToken(channelId, communityId) {
    return await this.request(`/api/voice/token?channel_id=${channelId}&community_id=${communityId}`);
  }

  /**
   * Get list of participants in a voice channel
   */
  async getVoiceParticipants(channelId) {
    return await this.request(`/api/voice/participants/${channelId}`);
  }

  // ============ Content Moderation ============

  /**
   * Moderate text content (comments, chat messages, usernames)
   * Uses Hugging Face martin-ha/toxic-comment-model
   * @param {string} text - Text to moderate
   * @param {string} contentType - 'text'|'comment'|'chat_message'|'username'|'community_post'
   * @returns {Promise<{action: string, is_safe: boolean, confidence: number}>}
   */
  async moderateText(text, contentType = 'text') {
    return await this.request('/api/moderation/text', {
      method: 'POST',
      body: JSON.stringify({ text, content_type: contentType })
    });
  }

  /**
   * Moderate a comment before posting
   * @param {string} text - Comment text
   * @returns {Promise<{allowed: boolean, action: string}>}
   */
  async moderateComment(text) {
    const formData = new FormData();
    formData.append('text', text);
    
    const token = this.getAuthToken();
    const url = joinUrl(this.baseURL, '/api/moderation/comment');
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Comment moderation failed');
    }
    return await response.json();
  }

  /**
   * Batch moderate multiple text items
   * @param {Array<{text: string, content_type: string}>} items
   * @returns {Promise<Array>}
   */
  async moderateTextBatch(items) {
    return await this.request('/api/moderation/text/batch', {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  }

  /**
   * Moderate an image file
   * Uses Hugging Face Falconsai/nsfw_image_detection
   * @param {File} file - Image file to moderate
   * @returns {Promise<{action: string, is_safe: boolean, rating: string}>}
   */
  async moderateImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this.getAuthToken();
    const url = joinUrl(this.baseURL, '/api/moderation/image');
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Image moderation failed');
    }
    return await response.json();
  }

  /**
   * Moderate a video file
   * @param {File} file - Video file to moderate
   * @param {number} sampleRate - Frames per second to check (default 1.0)
   * @returns {Promise<{action: string, is_safe: boolean, rating: string, nsfw_ratio: number}>}
   */
  async moderateVideo(file, sampleRate = 1.0) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this.getAuthToken();
    const url = joinUrl(this.baseURL, `/api/moderation/video?sample_rate=${sampleRate}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include'
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Video moderation failed');
    }
    return await response.json();
  }

  /**
   * Get moderation service settings and status
   * @returns {Promise<{mode: string, models: object, thresholds: object}>}
   */
  async getModerationSettings() {
    return await this.request('/api/moderation/settings');
  }

  /**
   * Check moderation service health
   * @returns {Promise<{status: string}>}
   */
  async getModerationHealth() {
    return await this.request('/api/moderation/health');
  }

  // ============================================================================
  // GAMIFICATION - XP, Badges, Achievements, Skills, Leaderboards
  // ============================================================================

  /**
   * Get user's XP stats
   * @param {number} userId - User ID
   * @returns {Promise<{total_xp: number, level: number, next_level_xp: number}>}
   */
  async getUserXP(userId) {
    return await this.request(`/api/xp/user/${userId}`);
  }

  /**
   * Grant XP to user
   * @param {number} userId - User ID  
   * @param {string} action - Action type (watch_video, like, comment, etc.)
   * @param {number} amount - XP amount
   * @returns {Promise<{success: boolean, new_total: number}>}
   */
  async grantXP(userId, action, amount) {
    return await this.request('/api/xp/grant', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, action, amount })
    });
  }

  /**
   * Get user's XP transaction history
   * @param {number} userId - User ID
   * @param {object} options - Pagination options
   * @returns {Promise<Array>}
   */
  async getXPHistory(userId, { limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/xp/user/${userId}/transactions?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get XP leaderboard
   * @param {object} options - Pagination and timeframe options
   * @returns {Promise<Array>}
   */
  async getXPLeaderboard({ limit = 20, offset = 0, timeframe = 'all' } = {}) {
    return await this.request(`/api/xp/leaderboard?limit=${limit}&offset=${offset}&timeframe=${timeframe}`);
  }

  /**
   * Update user's daily streak
   * @param {number} userId - User ID
   * @returns {Promise<{streak: number, streak_updated: boolean}>}
   */
  async updateStreak(userId) {
    return await this.request(`/api/xp/user/${userId}/streak/update`, { method: 'POST' });
  }

  /**
   * Get all available badges
   * @returns {Promise<Array>}
   */
  async getAllBadges() {
    return await this.request('/api/badges/');
  }

  /**
   * Get badge by ID
   * @param {number} badgeId - Badge ID
   * @returns {Promise<object>}
   */
  async getBadge(badgeId) {
    return await this.request(`/api/badges/${badgeId}`);
  }

  /**
   * Get user's earned badges
   * @param {number} userId - User ID
   * @returns {Promise<Array>}
   */
  async getUserBadges(userId) {
    return await this.request(`/api/badges/user/${userId}`);
  }

  /**
   * Award badge to user
   * @param {number} userId - User ID
   * @param {number} badgeId - Badge ID
   * @returns {Promise<{success: boolean}>}
   */
  async awardBadge(userId, badgeId) {
    return await this.request('/api/badges/award', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, badge_id: badgeId })
    });
  }

  /**
   * Check if user has specific badge
   * @param {number} userId - User ID
   * @param {number} badgeId - Badge ID
   * @returns {Promise<{has_badge: boolean}>}
   */
  async checkUserBadge(userId, badgeId) {
    return await this.request(`/api/badges/user/${userId}/check/${badgeId}`);
  }

  /**
   * Get user's badge stats (total earned, rarity breakdown)
   * @param {number} userId - User ID
   * @returns {Promise<object>}
   */
  async getUserBadgeStats(userId) {
    return await this.request(`/api/badges/stats/user/${userId}`);
  }

  /**
   * Get all available achievements
   * @returns {Promise<Array>}
   */
  async getAllAchievements() {
    return await this.request('/api/achievements/');
  }

  /**
   * Create new achievement (admin)
   * @param {object} data - Achievement data
   * @returns {Promise<object>}
   */
  async createAchievement(data) {
    return await this.request('/api/achievements/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get user's achievements
   * @param {number} userId - User ID
   * @returns {Promise<Array>}
   */
  async getUserAchievements(userId) {
    return await this.request(`/api/achievements/user/${userId}`);
  }

  /**
   * Award achievement to user
   * @param {number} userId - User ID
   * @param {string} achievementId - Achievement ID
   * @returns {Promise<{success: boolean}>}
   */
  async awardAchievement(userId, achievementId) {
    return await this.request('/api/achievements/user', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, achievement_id: achievementId })
    });
  }

  /**
   * Get user's achievement stats
   * @param {number} userId - User ID
   * @returns {Promise<object>}
   */
  async getUserAchievementStats(userId) {
    return await this.request(`/api/achievements/user/${userId}/stats`);
  }

  /**
   * Get user's skills
   * @param {number} userId - User ID
   * @returns {Promise<Array>}
   */
  async getUserSkills(userId) {
    return await this.request(`/api/achievements/skills/user/${userId}`);
  }

  /**
   * Check if user has unlocked a specific skill
   * @param {number} userId - User ID
   * @param {string} skillId - Skill ID
   * @returns {Promise<{unlocked: boolean, progress: number}>}
   */
  async checkUserSkill(userId, skillId) {
    return await this.request(`/api/achievements/skills/check/${userId}/${skillId}`);
  }

  /**
   * Create skill (admin)
   * @param {object} data - Skill data
   * @returns {Promise<object>}
   */
  async createSkill(data) {
    return await this.request('/api/achievements/skills', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get achievements leaderboard
   * @param {object} options - Pagination options
   * @returns {Promise<Array>}
   */
  async getAchievementsLeaderboard({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/achievements/leaderboard?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get combined gamification profile for user
   * @param {number} userId - User ID
   * @returns {Promise<{xp: object, badges: Array, achievements: Array, skills: Array}>}
   */
  async getGamificationProfile(userId) {
    const [xp, badges, achievements, skills, badgeStats, achievementStats] = await Promise.all([
      this.getUserXP(userId).catch(() => ({ total_xp: 0, level: 1, next_level_xp: 100 })),
      this.getUserBadges(userId).catch(() => []),
      this.getUserAchievements(userId).catch(() => []),
      this.getUserSkills(userId).catch(() => []),
      this.getUserBadgeStats(userId).catch(() => ({})),
      this.getUserAchievementStats(userId).catch(() => ({}))
    ]);
    
    return {
      xp,
      badges,
      achievements,
      skills,
      badgeStats,
      achievementStats
    };
  }

  // ==========================================================================
  // ART CENTER - Brushes, Sessions, Artworks
  // ==========================================================================

  /**
   * Get user's custom brushes
   * @param {number} userId - User ID (optional, defaults to current user)
   */
  async getUserBrushes(userId) {
    const endpoint = userId ? `/api/art/brushes?user_id=${userId}` : '/api/art/brushes';
    return await this.request(endpoint);
  }

  /**
   * Save a custom brush
   * @param {object} brushData - { name, settings, preview_image }
   */
  async saveBrush(brushData) {
    return await this.request('/api/art/brushes', {
      method: 'POST',
      body: JSON.stringify(brushData)
    });
  }

  /**
   * Delete a custom brush
   * @param {number} brushId - Brush ID
   */
  async deleteBrush(brushId) {
    return await this.request(`/api/art/brushes/${brushId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get art sessions (for continuing work)
   * @param {object} options - { limit, offset }
   */
  async getArtSessions({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/art/sessions?limit=${limit}&offset=${offset}`);
  }

  /**
   * Create or update an art session (autosave)
   * @param {object} sessionData - { canvas_data, thumbnail, title }
   */
  async saveArtSession(sessionData) {
    return await this.request('/api/art/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  /**
   * Get a specific art session
   * @param {number} sessionId - Session ID
   */
  async getArtSession(sessionId) {
    return await this.request(`/api/art/sessions/${sessionId}`);
  }

  /**
   * Delete an art session
   * @param {number} sessionId - Session ID
   */
  async deleteArtSession(sessionId) {
    return await this.request(`/api/art/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Publish artwork (from session or new)
   * @param {object} artworkData - { title, description, image_url, tags, visibility, session_id }
   */
  async publishArtwork(artworkData) {
    return await this.request('/api/art/artworks', {
      method: 'POST',
      body: JSON.stringify(artworkData)
    });
  }

  /**
   * Get artworks feed
   * @param {object} options - { limit, offset, user_id, tag }
   */
  async getArtworks({ limit = 20, offset = 0, userId, tag } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (userId) params.append('user_id', userId);
    if (tag) params.append('tag', tag);
    return await this.request(`/api/art/artworks?${params}`);
  }

  /**
   * Get single artwork details
   * @param {number} artworkId - Artwork ID
   */
  async getArtwork(artworkId) {
    return await this.request(`/api/art/artworks/${artworkId}`);
  }

  /**
   * Delete an artwork
   * @param {number} artworkId - Artwork ID
   */
  async deleteArtwork(artworkId) {
    return await this.request(`/api/art/artworks/${artworkId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Like/unlike an artwork
   * @param {number} artworkId - Artwork ID
   */
  async toggleArtworkLike(artworkId) {
    return await this.request(`/api/art/artworks/${artworkId}/like`, {
      method: 'POST'
    });
  }

  // ==========================================================================
  // READER'S NOOK - Storyboards, Chapters, Illustrations
  // ==========================================================================

  /**
   * Get user's storyboards (stories)
   * @param {object} options - { limit, offset, status }
   */
  async getStoryboards({ limit = 20, offset = 0, status } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.append('status', status);
    return await this.request(`/api/stories/storyboards?${params}`);
  }

  /**
   * Create a new storyboard
   * @param {object} data - { title, description, genre, tags, cover_image, maturity_rating }
   */
  async createStoryboard(data) {
    return await this.request('/api/stories/storyboards', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get storyboard details
   * @param {number} storyboardId - Storyboard ID
   */
  async getStoryboard(storyboardId) {
    return await this.request(`/api/stories/storyboards/${storyboardId}`);
  }

  /**
   * Update storyboard metadata
   * @param {number} storyboardId - Storyboard ID
   * @param {object} data - Updated fields
   */
  async updateStoryboard(storyboardId, data) {
    return await this.request(`/api/stories/storyboards/${storyboardId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a storyboard
   * @param {number} storyboardId - Storyboard ID
   */
  async deleteStoryboard(storyboardId) {
    return await this.request(`/api/stories/storyboards/${storyboardId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Publish a storyboard
   * @param {number} storyboardId - Storyboard ID
   */
  async publishStoryboard(storyboardId) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/publish`, {
      method: 'POST'
    });
  }

  /**
   * Get chapters for a storyboard
   * @param {number} storyboardId - Storyboard ID
   */
  async getChapters(storyboardId) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/chapters`);
  }

  /**
   * Create a new chapter
   * @param {number} storyboardId - Storyboard ID
   * @param {object} data - { title, content, chapter_number }
   */
  async createChapter(storyboardId, data) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a chapter
   * @param {number} storyboardId - Storyboard ID
   * @param {number} chapterId - Chapter ID
   * @param {object} data - Updated fields
   */
  async updateChapter(storyboardId, chapterId, data) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/chapters/${chapterId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a chapter
   * @param {number} storyboardId - Storyboard ID
   * @param {number} chapterId - Chapter ID
   */
  async deleteChapter(storyboardId, chapterId) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/chapters/${chapterId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Add illustration to a chapter
   * @param {number} storyboardId - Storyboard ID
   * @param {number} chapterId - Chapter ID
   * @param {object} data - { image_url, caption, position }
   */
  async addIllustration(storyboardId, chapterId, data) {
    return await this.request(`/api/stories/storyboards/${storyboardId}/chapters/${chapterId}/illustrations`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get published stories feed
   * @param {object} options - { limit, offset, genre, sort }
   */
  async getPublishedStories({ limit = 20, offset = 0, genre, sort = 'recent' } = {}) {
    const params = new URLSearchParams({ limit, offset, sort });
    if (genre) params.append('genre', genre);
    return await this.request(`/api/stories/feed?${params}`);
  }

  /**
   * Get story reader view (for reading published stories)
   * @param {number} storyId - Story ID
   */
  async getStoryReader(storyId) {
    return await this.request(`/api/stories/${storyId}/read`);
  }

  /**
   * Bookmark a story
   * @param {number} storyId - Story ID
   */
  async toggleStoryBookmark(storyId) {
    return await this.request(`/api/stories/${storyId}/bookmark`, {
      method: 'POST'
    });
  }

  /**
   * Get user's bookmarked stories
   */
  async getBookmarkedStories({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/stories/bookmarks?limit=${limit}&offset=${offset}`);
  }

  // ==========================================================================
  // SKILL TREES - Full skill tree system
  // ==========================================================================

  /**
   * Get all skill tree categories
   */
  async getSkillTrees() {
    return await this.request('/api/skill-trees/categories');
  }

  /**
   * Get skill trees in a specific category
   * @param {string} categoryKey - Category key
   */
  async getSkillTreeCategory(categoryKey) {
    return await this.request(`/api/skill-trees/categories/${categoryKey}`);
  }

  /**
   * Get a specific skill tree with user progress
   * @param {string} treeKey - Skill tree key
   */
  async getSkillTree(treeKey) {
    return await this.request(`/api/skill-trees/tree/${treeKey}`);
  }

  /**
   * Get user's skill tree progress
   * @param {number} userId - User ID
   */
  async getSkillTreeProgress(userId) {
    return await this.request(`/api/skill-trees/user/${userId}/progress`);
  }

  /**
   * Unlock a skill in a tree
   * @param {string} skillKey - Skill key
   */
  async unlockSkill(skillKey) {
    return await this.request('/api/skill-trees/unlock', {
      method: 'POST',
      body: JSON.stringify({ skill_key: skillKey })
    });
  }

  /**
   * Update skill progress
   * @param {string} skillKey - Skill key
   * @param {number} xpGained - XP to add
   */
  async updateSkillProgress(skillKey, xpGained) {
    return await this.request('/api/skill-trees/progress', {
      method: 'POST',
      body: JSON.stringify({ skill_key: skillKey, xp_gained: xpGained })
    });
  }

  /**
   * Get skill tree leaderboard
   * @param {string} treeKey - Tree key
   */
  async getSkillTreeLeaderboard(treeKey, { limit = 20 } = {}) {
    return await this.request(`/api/skill-trees/leaderboard/${treeKey}?limit=${limit}`);
  }

  // ==========================================================================
  // CACHE & CATEGORIES - Chunked loading support
  // ==========================================================================

  /**
   * Get community categories for chunked loading
   */
  async getCommunityCategories() {
    return await this.request('/api/communities/categories');
  }

  /**
   * Get communities by category
   * @param {object} options - { category, limit, offset }
   */
  async getCommunitiesByCategory({ category = 'all', limit = 12, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (category && category !== 'all') params.append('category', category);
    return await this.request(`/api/communities?${params}`);
  }

  /**
   * Get free agent categories
   */
  async getFreeAgentCategories() {
    return await this.request('/api/users/free-agents/categories');
  }

  /**
   * Get free agents by category
   * @param {object} options - { category, limit, offset }
   */
  async getFreeAgentsByCategory({ category = 'all', limit = 12, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (category && category !== 'all') params.append('category', category);
    return await this.request(`/api/users/free-agents?${params}`);
  }

  // ==========================================================================
  // LIVE STREAMING
  // ==========================================================================

  /**
   * Start a live stream
   * @param {object} data - { title, description, community_id }
   */
  async startLiveStream(data) {
    return await this.request('/api/live/start', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * End a live stream
   * @param {number} sessionId - Stream session ID
   */
  async endLiveStream(sessionId) {
    return await this.request('/api/live/end', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId })
    });
  }

  /**
   * Get active live streams
   * @param {object} options - { limit, offset }
   */
  async getActiveLiveStreams({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/live/streams/active?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get live stream details
   * @param {number} sessionId - Stream session ID
   */
  async getLiveStream(sessionId) {
    return await this.request(`/api/live/${sessionId}`);
  }

  /**
   * Send a gift to a streamer
   * @param {object} data - { stream_id, streamer_id, gift_type, coins }
   */
  async sendStreamGift(data) {
    return await this.request('/api/live/gifts/send', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get gift leaderboard for a stream
   * @param {number} streamId - Stream ID
   * @param {object} options - { limit }
   */
  async getStreamGiftLeaderboard(streamId, { limit = 10 } = {}) {
    return await this.request(`/api/live/${streamId}/gifts/leaderboard?limit=${limit}`);
  }

  /**
   * Get recent gifts for a stream
   * @param {number} streamId - Stream ID
   * @param {object} options - { limit }
   */
  async getRecentStreamGifts(streamId, { limit = 20 } = {}) {
    return await this.request(`/api/live/${streamId}/gifts/recent?limit=${limit}`);
  }

  // ==========================================================================
  // MUSIC FEED - DAW publishing and music discovery
  // ==========================================================================

  /**
   * Publish a music track from DAW
   * @param {object} trackData - Track details
   */
  async publishMusicTrack(trackData) {
    return await this.request('/api/music/tracks', {
      method: 'POST',
      body: JSON.stringify(trackData)
    });
  }

  /**
   * Get music feed
   * @param {object} options - { genre, community_id, user_id, sort, limit, offset }
   */
  async getMusicFeed({ genre, communityId, userId, sort = 'recent', limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams({ sort, limit, offset });
    if (genre) params.append('genre', genre);
    if (communityId) params.append('community_id', communityId);
    if (userId) params.append('user_id', userId);
    return await this.request(`/api/music/tracks?${params}`);
  }

  /**
   * Get single track details
   * @param {number} trackId - Track ID
   */
  async getMusicTrack(trackId) {
    return await this.request(`/api/music/tracks/${trackId}`);
  }

  /**
   * Record a play for a track
   * @param {number} trackId - Track ID
   */
  async recordTrackPlay(trackId) {
    return await this.request(`/api/music/tracks/${trackId}/play`, {
      method: 'POST'
    });
  }

  /**
   * Like/unlike a track
   * @param {number} trackId - Track ID
   */
  async toggleTrackLike(trackId) {
    return await this.request(`/api/music/tracks/${trackId}/like`, {
      method: 'POST'
    });
  }

  /**
   * Get music genres with counts
   */
  async getMusicGenres() {
    return await this.request('/api/music/genres');
  }

  /**
   * Get user's music tracks
   * @param {number} userId - User ID
   * @param {object} options - { includePrivate, limit }
   */
  async getUserTracks(userId, { includePrivate = false, limit = 50 } = {}) {
    const params = new URLSearchParams({ limit });
    if (includePrivate) params.append('include_private', 'true');
    return await this.request(`/api/music/user/${userId}?${params}`);
  }

  /**
   * Record music track usage in a post
   * @param {number} trackId - Track ID
   * @param {number} postId - Post ID
   */
  async recordTrackUsage(trackId, postId) {
    return await this.request(`/api/music/tracks/${trackId}/use`, {
      method: 'POST',
      body: JSON.stringify({ post_id: postId })
    });
  }

  // ==========================================================================
  // CROSS-MEDIA POSTS - Unified post creation with linked content
  // ==========================================================================

  /**
   * Create a cross-media post with optional music, art, story attachments
   * @param {object} postData - Post data with cross-media references
   */
  async createCrossMediaPost(postData) {
    return await this.request('/api/posts/create', {
      method: 'POST',
      body: JSON.stringify(postData)
    });
  }

  /**
   * Get unified feed with type filters
   * @param {object} options - { feedType, contentType, communityId, limit, offset }
   */
  async getUnifiedFeed({ feedType, contentType, communityId, limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (feedType) params.append('feed_type', feedType);
    if (contentType) params.append('content_type', contentType);
    if (communityId) params.append('community_id', communityId);
    return await this.request(`/api/posts/feed?${params}`);
  }

  /**
   * Get music tracks available for use in videos/posts
   * @param {object} options - { userId, communityId, limit, offset }
   */
  async getAvailableMusic({ userId, communityId, limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (userId) params.append('user_id', userId);
    if (communityId) params.append('community_id', communityId);
    return await this.request(`/api/posts/available-music?${params}`);
  }

  /**
   * Get artwork available for use in videos/posts
   * @param {object} options - { userId, communityId, limit, offset }
   */
  async getAvailableArtwork({ userId, communityId, limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (userId) params.append('user_id', userId);
    if (communityId) params.append('community_id', communityId);
    return await this.request(`/api/posts/available-artwork?${params}`);
  }

  // ==========================================================================
  // MINI GAMES - Game catalog, sessions, leaderboards
  // ==========================================================================

  /**
   * Get mini games catalog
   * @param {object} options - { limit, offset }
   */
  async getGamesCatalog({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/games/catalog?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get game details
   * @param {string} gameId - Game ID
   */
  async getGameDetails(gameId) {
    return await this.request(`/api/games/${gameId}`);
  }

  /**
   * Create a new game session
   * @param {string} gameId - Game ID
   */
  async createGameSession(gameId) {
    return await this.request('/api/games/sessions', {
      method: 'POST',
      body: JSON.stringify({ game_id: gameId })
    });
  }

  /**
   * End a game session and submit score
   * @param {string} sessionId - Session ID
   * @param {number} score - Final score
   */
  async endGameSession(sessionId, score) {
    return await this.request(`/api/games/sessions/${sessionId}/end`, {
      method: 'POST',
      body: JSON.stringify({ score })
    });
  }

  /**
   * Get game leaderboard
   * @param {string} gameId - Game ID (or 'global' for global leaderboard)
   * @param {object} options - { limit, offset, timeframe }
   */
  async getGameLeaderboard(gameId = 'global', { limit = 20, offset = 0, timeframe = 'weekly' } = {}) {
    const params = new URLSearchParams({ limit, offset, timeframe });
    return await this.request(`/api/games/${gameId}/leaderboard?${params}`);
  }

  /**
   * Get user's game history
   * @param {object} options - { limit, offset }
   */
  async getGameHistory({ limit = 20, offset = 0 } = {}) {
    return await this.request(`/api/games/history?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get user's game stats
   */
  async getGameStats() {
    return await this.request('/api/games/stats');
  }
}

// Export both the class and a singleton instance
export { AIIAAPIService };
export default new AIIAAPIService();
